import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { SettableMetadata } from 'firebase/storage';
import { db } from './config';

export interface UploadedByInfo {
  id?: string;
  email?: string;
  name?: string;
}

export interface DocumentUploadContext {
  docType: string;
  organizationId?: string;
  locationId?: string;
  taskId?: string;
  templateId?: string;
  checklistId?: string;
  formId?: string;
  agreementId?: string;
  assetId?: string;
  uploadedBy?: UploadedByInfo;
  title?: string;
  source?: 'web' | 'mobile' | 'service';
  additionalMetadata?: Record<string, string | number | boolean | undefined | null>;
}

export interface DocumentMetadataRecord {
  storagePath: string;
  downloadUrl: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedAt: string;
  bucket?: string;
  context: DocumentUploadContext;
}

const DOCUMENTS_COLLECTION = 'documents';

const toStringValue = (value: string | number | boolean | undefined | null): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (err) {
    console.warn('documentRegistry: failed to serialize metadata value', err);
    return undefined;
  }
};

const buildCustomMetadata = (context: DocumentUploadContext, uploadedAt: string): Record<string, string> => {
  const customMetadata: Record<string, string> = {};
  const add = (key: string, value?: string) => {
    if (value && value.trim() !== '') {
      customMetadata[key] = value;
    }
  };

  add('docType', context.docType);
  add('organizationId', context.organizationId);
  add('locationId', context.locationId);
  add('taskId', context.taskId);
  add('templateId', context.templateId);
  add('checklistId', context.checklistId);
  add('formId', context.formId);
  add('agreementId', context.agreementId);
  add('assetId', context.assetId);
  add('uploadedById', context.uploadedBy?.id);
  add('uploadedByEmail', context.uploadedBy?.email);
  add('uploadedByName', context.uploadedBy?.name);
  add('uploadedAt', uploadedAt);
  add('title', context.title);
  add('source', context.source);

  if (context.additionalMetadata) {
    Object.entries(context.additionalMetadata).forEach(([key, value]) => {
      const strValue = toStringValue(value);
      if (strValue !== undefined) {
        add(key, strValue);
      }
    });
  }

  return customMetadata;
};

export function createStorageMetadata(
  context: DocumentUploadContext,
  options: { contentType?: string; uploadedAt?: string } = {}
): SettableMetadata {
  const uploadedAt = options.uploadedAt || new Date().toISOString();
  const metadata: SettableMetadata = {};

  const customMetadata = buildCustomMetadata(context, uploadedAt);
  if (Object.keys(customMetadata).length > 0) {
    metadata.customMetadata = customMetadata;
  }
  if (options.contentType) {
    metadata.contentType = options.contentType;
  }

  return metadata;
}

const sanitizeDocId = (storagePath: string): string =>
  storagePath.replace(/[*/[\]#.]/g, '_');

const cleanPayload = (payload: Record<string, unknown>) => {
  const result: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      const nested = cleanPayload(value as Record<string, unknown>);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
      return;
    }
    result[key] = value;
  });
  return result;
};

export async function recordDocumentMetadata(metadata: DocumentMetadataRecord): Promise<void> {
  if (!db) {
    console.warn('documentRegistry: Firestore not initialized; skipping metadata write.');
    return;
  }

  try {
    const uploadedAtIso = metadata.uploadedAt;
    const docId = sanitizeDocId(metadata.storagePath);
    const docRef = doc(db, DOCUMENTS_COLLECTION, docId);

    const payload = cleanPayload({
      storagePath: metadata.storagePath,
      downloadUrl: metadata.downloadUrl,
      mimeType: metadata.mimeType,
      sizeBytes: metadata.sizeBytes,
      bucket: metadata.bucket,
      uploadedAt: uploadedAtIso,
      uploadedAtTimestamp: serverTimestamp(),
      ...metadata.context,
      docType: metadata.context.docType,
      organizationId: metadata.context.organizationId,
      locationId: metadata.context.locationId,
      taskId: metadata.context.taskId,
      templateId: metadata.context.templateId,
      checklistId: metadata.context.checklistId,
      formId: metadata.context.formId,
      agreementId: metadata.context.agreementId,
      assetId: metadata.context.assetId,
      uploadedBy: metadata.context.uploadedBy,
      title: metadata.context.title,
      source: metadata.context.source,
      additionalMetadata: metadata.context.additionalMetadata,
    });

    await setDoc(docRef, payload, { merge: true });
  } catch (error) {
    console.error('documentRegistry: failed to record document metadata', error);
  }
}


